import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { dequal } from 'dequal';
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { ANALYTICS } from '@/routes/apiEndpoints';
import { useIsFirstStatsLoaded } from '@/hooks/socialprofiles/useIsFirstStatsLoaded';

const fetcher = async (url, requestParams) => {
    if (requestParams?.fields?.[0] && Array.isArray(requestParams?.fields?.[0])) {
        const responses = await Promise.all(
            requestParams?.fields.map((fields) =>
                get(url, { ...requestParams, fields }).then((res) => res.data)
            )
        );

        const data = responses.reduce(function (result, current) {
            const resultAnalytics = result?.analytics ?? {};
            const currentAnalytics = current?.analytics ?? {};
            return Object.assign(result, {
                ...current,
                analytics: { ...resultAnalytics, ...currentAnalytics }
            });
        }, {});

        return data;
    }

    return get(url, requestParams).then((res) => res.data);
};

const useAnalytics = (activeSPID, params = {}, options = {}) => {
    const firstLoad = useRef(null);
    const [state, setState] = useState(null);
    const [shouldReFetch, setShouldReFetch] = useState(false);
    const isFirstStatsLoaded = useIsFirstStatsLoaded({ SPID: activeSPID });

    if (params?.period !== 'custom') {
        delete params?.start;
        delete params?.end;
    }

    if (params?.album_id) {
        delete params?.period;
        delete params?.comparison_period;
        delete params?.start;
        delete params?.end;
    } else {
        delete params?.album_id;
    }

    const [requestParams, setRequestParams] = useState(params);

    const {
        data,
        mutate: mutateAnalytics,
        isValidating: isValidatingAnalytics,
        error: analyticsError
    } = useSWR(
        //the second part of this is needed to ensure that no unnecessary request is sent
        isFirstStatsLoaded && requestParams && (dequal(params, requestParams) || shouldReFetch)
            ? [
                  injectEndpointUrlParams(ANALYTICS, { SPID: activeSPID }),
                  ...Object.values(requestParams)
              ]
            : null,
        ([url]) => fetcher(url, requestParams),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    useEffect(() => {
        if (dequal(params, requestParams)) {
            // switch back shouldRefresh to false
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        setRequestParams(params);
        firstLoad.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSPID, params?.period, params?.start, params?.end, params?.comparison_period]);

    // setShouldReFetch based on requestParams
    useEffect(() => {
        if (dequal(params, requestParams)) {
            // switch back shouldRefresh to false
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        //make sure we don't set shouldReFetch if it's not needed
        if (shouldReFetch) return;
        setShouldReFetch(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestParams]);

    useEffect(() => {
        if (!data?.analytics && !analyticsError) {
            return;
        }
        firstLoad.current = true;
        setState(data?.analytics);
    }, [analyticsError, data?.analytics]);

    return {
        analytics: state,
        dates: data?.dates,
        mutateAnalytics,
        analyticsError,
        setRequestParams,
        setState,
        isValidatingAnalytics,
        isLoading: (!state && !analyticsError) || !firstLoad.current
    };
};
export default useAnalytics;
